/* OCC: same-session cart variant changes. No app, discounts or inventory are simulated.
 * Add the new option first; remove the original only after fresh cart verification.
 * A failed POST can have committed (even HTTP 422); never retry/rollback it blindly.
 */
(function (global) {
  'use strict';
  const copy = value => JSON.parse(JSON.stringify(value));
  const id = value => /^\d+$/.test(String(value)) && Number.isSafeInteger(Number(value)) && Number(value) > 0;
  const properties = item => Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b));
  const signature = item => JSON.stringify([String(item.variant_id || item.id), properties(item), String(item.selling_plan_allocation?.selling_plan?.id || '')]);
  const quantity = (cart, sig) => cart.items.filter(item => signature(item) === sig).reduce((n, item) => n + item.quantity, 0);
  class CartEditError extends Error {
    constructor(message, code, review = false) { super(message); this.name = 'CartEditError'; this.code = code; this.review = review; }
  }
  function protectedReason(item, cart) {
    if (item.selling_plan_allocation) return 'Subscription options must be changed through the product or subscription service.';
    if (item.gift_card || item.parent_relationship || item.has_components || item.item_components?.length) return 'This gift card or bundle needs its original product flow.';
    if (Object.keys(item.properties || {}).some(key => key.startsWith('_'))) return 'This item has linked fulfilment or offer details. Use its original product flow.';
    if (cart.items.some(other => String(other.properties?._linked_to_product || '') === String(item.product_id))) return 'This item has a linked gift. Its gift rules need to be reviewed together.';
    return '';
  }
  function create({fetch: request, root = '/', timeout = 12000, shared = global} = {}) {
    if (!request) request = global.fetch.bind(global);
    if (!/^\/(?!\/)[a-zA-Z0-9/_-]*$/.test(root)) root = '/';
    if (!root.endsWith('/')) root += '/';
    let locked = false, reviewRequired = false;
    async function json(path, body) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeout);
      try {
        const response = await request(root + path, {
          method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
          headers: {'Accept': 'application/json', ...(body ? {'Content-Type': 'application/json'} : {})},
          ...(body ? {body: JSON.stringify(body)} : {})
        });
        const data = await response.json();
        if (!response.ok || Number(data.status) >= 400 || data.errors) {
          const detail = typeof data.description === 'string' ? data.description : typeof data.errors === 'string' ? data.errors : 'The store could not complete that request.';
          throw new CartEditError(detail, 'platform', Boolean(body));
        }
        return data;
      } catch (error) {
        if (error instanceof CartEditError) throw error;
        throw new CartEditError(body ? 'The update could not be confirmed. Review your cart before trying again.' : 'Cart details could not be loaded. Please try again.', 'network', Boolean(body));
      } finally { clearTimeout(timer); }
    }
    async function cart() {
      const data = await json('cart.js');
      if (!Array.isArray(data.items) || data.items.some(item => !item.key || !id(item.variant_id || item.id) || !Number.isSafeInteger(item.quantity) || item.quantity < 1)) {
        throw new CartEditError('The cart response was incomplete. Refresh the cart before changing it.', 'invalid');
      }
      return data;
    }
    function line(cartData, key, expected) {
      const item = cartData.items.find(item => item.key === key);
      if (!item || (expected && (signature(item) !== signature(expected) || item.quantity !== expected.quantity))) {
        throw new CartEditError('This cart item changed in another action. Refresh the cart and choose again.', 'stale');
      }
      const reason = protectedReason(item, cartData);
      if (reason) throw new CartEditError(reason, 'protected');
      return item;
    }
    async function inspect(key, handle) {
      if (!/^[a-z0-9][a-z0-9-]{0,254}$/i.test(handle)) throw new CartEditError('This product link is not available.', 'invalid');
      const before = await cart();
      const item = line(before, key);
      const product = await json('products/' + handle + '.js');
      if (!Array.isArray(product.variants) || String(product.id) !== String(item.product_id) || !product.variants.some(v => String(v.id) === String(item.variant_id))) {
        throw new CartEditError('The current option could not be verified. Open the product for its latest options.', 'invalid');
      }
      return {item: copy(item), product};
    }
    async function replace(expected, nextVariantId, handle) {
      if (locked || shared.occCartMutationBusy) throw new CartEditError('Another cart update is in progress. Please wait.', 'busy');
      if (reviewRequired) throw new CartEditError('Review the latest cart before making another change.', 'review', true);
      if (!expected || !id(nextVariantId)) throw new CartEditError('Choose an available option.', 'invalid');
      if (String(expected.variant_id) === String(nextVariantId)) return {changed: false};
      locked = true; const owner = {}; shared.occCartMutationBusy = owner;
      let attempted = false, added = false;
      try {
        const current = await inspect(expected.key, handle);
        if (signature(current.item) !== signature(expected) || current.item.quantity !== expected.quantity) throw new CartEditError('This cart item has changed. Refresh and choose again.', 'stale');
        const option = current.product.variants.find(v => String(v.id) === String(nextVariantId));
        if (!option || !option.available) throw new CartEditError('That option is no longer available. Your original item has not been changed.', 'unavailable');
        // Re-read after product lookup; another tab may have changed the cart meanwhile.
        const before = await cart(); const original = line(before, expected.key, expected);
        const desired = {...original, variant_id: Number(nextVariantId)};
        const originalSig = signature(original), desiredSig = signature(desired);
        const originalTotal = quantity(before, originalSig), desiredTotal = quantity(before, desiredSig);
        attempted = true;
        await json('cart/add.js', {items: [{id: Number(nextVariantId), quantity: original.quantity, properties: copy(original.properties || {})}]});
        added = true;
        const afterAdd = await cart();
        const remaining = afterAdd.items.filter(item => signature(item) === originalSig);
        // Keys may change when automatic discounts apply. Only accept an unambiguous exact identity.
        const sameKey = remaining.find(item => item.key === original.key);
        const removable = sameKey || (remaining.length === 1 ? remaining[0] : null);
        if (!removable || removable.quantity !== original.quantity || quantity(afterAdd, originalSig) !== originalTotal || quantity(afterAdd, desiredSig) !== desiredTotal + original.quantity) {
          throw new CartEditError('The new option was added, but the cart changed before the original could be removed. Review both options in your cart.', 'changed', true);
        }
        const reason = protectedReason(removable, afterAdd);
        if (reason) throw new CartEditError('The new option was added, but linked offer details changed. Review your cart; the original was not removed.', 'protected', true);
        await json('cart/change.js', {id: removable.key, quantity: 0});
        const after = await cart();
        if (quantity(after, originalSig) !== originalTotal - original.quantity || quantity(after, desiredSig) !== desiredTotal + original.quantity) {
          throw new CartEditError('The store updated the cart, but the final quantities need review. Check your cart before continuing.', 'changed', true);
        }
        reviewRequired = true; // Force a fresh native render before editing again; never display stale totals.
        return {changed: true, cart: after, option: copy(option)};
      } catch (error) {
        if (attempted) {
          reviewRequired = true;
          if (!error.review) error = new CartEditError((added ? 'The new option may be in your cart. ' : '') + error.message + ' Review your cart before trying again.', error.code || 'unknown', true);
          else if (error.code === 'platform') error = new CartEditError(error.message + ' The cart may have changed. Review it before trying again; the editor will not repeat the request.', error.code, true);
        }
        throw error;
      } finally {
        locked = false;
        if (shared.occCartMutationBusy === owner) shared.occCartMutationBusy = false;
      }
    }
    return {inspect, replace, readCart: cart, request: json, needsReview: () => reviewRequired, busy: () => locked};
  }
  global.OCCCartVariantCore = {create, signature, protectedReason};
})(typeof window === 'undefined' ? globalThis : window);
